const ANIMATION_OPTIONS = {
  duration: 500,
};

class MarqueeComponent extends HTMLElement {
  #animation = null;
  #marqueeWidth = null;
  #resizeDebounceTimer = null;
  #slowDownTimer = null;

  connectedCallback() {
    this._wrapper = this.querySelector('[ref="wrapper"]');
    this._content = this.querySelector('[ref="content"]');
    this._marqueeItems = Array.from(this.querySelectorAll('[ref="marqueeItems[]"]'));

    if (!this._wrapper || !this._content || this._marqueeItems.length === 0) return;

    this.#init();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  async #init() {
    const { numberOfCopies } = await this.#queryNumberOfCopies();
    const speed = this.#calculateSpeed(numberOfCopies);
    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();
    this.#setSpeed(speed);

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp.bind(this));
  }

  #slowDown = () => {
    clearTimeout(this.#slowDownTimer);
    this.#slowDownTimer = setTimeout(() => {
      if (this.#animation) return;
      const animation = this._wrapper?.getAnimations()[0];
      if (!animation) return;
      this.#animation = animateValue({
        ...ANIMATION_OPTIONS,
        from: 1, to: 0,
        onUpdate: (v) => animation.updatePlaybackRate(v),
        onComplete: () => { this.#animation = null; },
      });
    }, ANIMATION_OPTIONS.duration);
  }

  #speedUp() {
    clearTimeout(this.#slowDownTimer);
    const animation = this._wrapper?.getAnimations()[0];
    if (!animation || animation.playbackRate === 1) return;
    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();
    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from, to: 1,
      onUpdate: (v) => animation.updatePlaybackRate(v),
      onComplete: () => { this.#animation = null; },
    });
  }

  get clonedContent() {
    const lastChild = this._wrapper?.lastElementChild;
    return this._content !== lastChild ? lastChild : null;
  }

  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    return new Promise((resolve) => {
      if (!this._marqueeItems[0]) {
        return setTimeout(() => resolve({ numberOfCopies: 1, isHorizontalResize: true }), 0);
      }
      const io = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        io.disconnect();
        const { width: marqueeWidth } = entry.rootBounds ?? { width: 0 };
        const { width: itemsWidth } = entry.boundingClientRect;
        const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
        this.#marqueeWidth = marqueeWidth;
        setTimeout(() => resolve({
          numberOfCopies: itemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / itemsWidth),
          isHorizontalResize,
        }), 0);
      }, { root: this });
      io.observe(this._marqueeItems[0]);
    });
  }

  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 25;
    return Math.sqrt(numberOfCopies) * speedFactor;
  }

  #handleResize = () => {
    clearTimeout(this.#resizeDebounceTimer);
    this.#resizeDebounceTimer = setTimeout(async () => {
      const { numberOfCopies: newCount, isHorizontalResize } = await this.#queryNumberOfCopies();
      if (!isHorizontalResize) return;
      const currentCount = this._marqueeItems.length;
      const speed = this.#calculateSpeed(newCount);
      if (newCount > currentCount) this.#addRepeatedItems(newCount - currentCount);
      else if (newCount < currentCount) this.#removeRepeatedItems(currentCount - newCount);
      this.#duplicateContent();
      this.#setSpeed(speed);
      this.#restartAnimation();
    }, 250);
  }

  #restartAnimation() {
    const animations = this._wrapper?.getAnimations() ?? [];
    requestAnimationFrame(() => {
      for (const anim of animations) anim.currentTime = 0;
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();
    const clone = this._content.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');
    this._wrapper.appendChild(clone);
  }

  #addRepeatedItems(numberOfCopies) {
    if (!this._marqueeItems[0]) return;
    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = this._marqueeItems[0].cloneNode(true);
      this._content.appendChild(clone);
    }
  }

  #removeRepeatedItems(numberOfCopies) {
    const children = Array.from(this._content.children);
    const toRemove = Math.min(numberOfCopies, children.length - 1);
    for (let i = 0; i < toRemove; i++) this._content.lastElementChild?.remove();
  }
}

function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  function animate(currentTime) {
    if (cancelled) return;
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    currentValue = from + (to - from) * easing(progress);
    onUpdate(currentValue);
    if (progress < 1) requestAnimationFrame(animate);
    else if (typeof onComplete === 'function') onComplete();
  }

  requestAnimationFrame(animate);
  return {
    get current() { return currentValue; },
    cancel() { cancelled = true; },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
